/**
 * Auth Context
 * 
 * 인증 상태 관리 (GitHub OAuth, 이메일/비밀번호)
 */

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import * as Sentry from '@sentry/react';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '';

// Sentry 사용자 정보 설정 헬퍼
function setSentryUser(user: { id: number; email: string; name: string | null; subscription_plan: string } | null) {
  if (user) {
    Sentry.setUser({
      id: String(user.id),
      email: user.email,
      username: user.name || undefined,
    });
    Sentry.setTag('plan', user.subscription_plan);
  } else {
    Sentry.setUser(null);
    Sentry.setTag('plan', 'anonymous');
  }
}

// 사용자 정보 타입
export interface User {
  id: number;
  public_id: string;  // 외부 노출용 고유 ID
  email: string;
  name: string | null;
  picture: string | null;
  phone: string | null;
  company: string | null;
  domain: string | null;
  position: string | null;
  subscription_plan: 'free' | 'pro' | 'max' | 'team';
  monthly_eval_count: number;
  terms_agreed: boolean;
  privacy_agreed: boolean;
  marketing_agreed: boolean;
  created_at: string;
  team_id: number | null;
  google_connected?: boolean;
  github_connected?: boolean;
  github_username?: string | null;
  has_password?: boolean;  // 비밀번호 설정 여부
  email_verified?: boolean;  // 이메일 인증 완료 여부
}

// Quota 정보 타입
export interface QuotaInfo {
  plan: 'free' | 'pro' | 'max' | 'team';
  actual_plan?: 'free' | 'pro' | 'max' | 'team';  // 본인 실제 플랜
  monthly_evaluations: {
    used: number;
    limit: number;
    unlimited: boolean;
  };
  limits: {
    max_test_cases: number;
    max_planning_chars: number;
    history_limit: number;
    pdf_download: boolean;
    api_access: boolean;
    max_testcase_files: number;   // 테스트케이스 파일 동시 처리 개수
    max_planning_files: number;   // 기획서 파일 동시 처리 개수
    personal_trend?: boolean;     // 개인 트렌드 분석 가능 여부
    team_trend?: boolean;         // 팀 트렌드 분석 가능 여부
    team_dashboard?: boolean;     // 팀 대시보드 가능 여부
  };
  team?: {
    team_id: number;
    is_owner: boolean;
    owner_plan: string;
    my_role: 'owner' | 'manager' | 'member';
  } | null;
  // API 키 사용량 정보 (Team 플랜만)
  api_usage?: {
    total_daily_usage: number;
    total_daily_limit: number;
    key_count: number;
  } | null;
}

// 프로필 업데이트 타입
export interface ProfileUpdate {
  name?: string;
  phone?: string;
  company?: string;
  domain?: string;
  position?: string;
}

// 약관 동의 타입
export interface TermsAgreement {
  terms_agreed: boolean;
  privacy_agreed: boolean;
  marketing_agreed: boolean;
}

// 이메일 체크 응답 타입
export interface EmailCheckResult {
  exists: boolean;
  has_password: boolean;
  google_connected: boolean;
  github_connected: boolean;
}

// 이메일 회원가입 1단계 응답 타입
export interface EmailRegisterResult {
  message: string;
  email: string;
  otp?: string;  // Local 환경에서만 노출
}

// Context 타입
interface AuthContextType {
  user: User | null;
  quota: QuotaInfo | null;
  isLoading: boolean;
  isLoggingIn: boolean;  // 로그인 처리 중 (딤 오버레이용)
  startLoggingIn: () => void;   // 딤 오버레이 시작
  cancelLoggingIn: () => void;  // 로그인 취소 시 딤 오버레이 종료
  isAuthenticated: boolean;
  // 로그아웃
  logout: () => void;
  // GitHub 로그인 시작 (redirect)
  startGithubLogin: () => void;
  // GitHub 약관 동의 필요 시 preauth 정보
  githubPreauth: GithubPreauth | null;
  // GitHub 약관 동의 완료(회원가입/로그인)
  completeGithubRegister: (preauthToken: string, terms: TermsAgreement) => Promise<void>;
  clearGithubPreauth: () => void;
  // 프로필 업데이트
  updateProfile: (profile: ProfileUpdate) => Promise<void>;
  // 회원 탈퇴
  deleteAccount: () => Promise<void>;
  // quota 새로고침
  refreshQuota: () => Promise<void>;
  // 사용자 정보 새로고침
  refreshUser: () => Promise<void>;
  // 에러 상태
  error: string | null;
  clearError: () => void;
  // 개발용 플랜 오버라이드 (local/staging 환경에서만)
  devPlanOverride: 'free' | 'pro' | 'max' | 'team' | null;
  setDevPlanOverride: (plan: 'free' | 'pro' | 'max' | 'team' | null) => void;
  
  // ===== 이메일/비밀번호 인증 =====
  // 이메일 존재 여부 확인
  checkEmail: (email: string) => Promise<EmailCheckResult>;
  // 이메일 회원가입 1단계 (OTP 발송)
  startEmailRegister: (email: string, password: string, name?: string, lang?: string) => Promise<EmailRegisterResult>;
  // 이메일 회원가입 2단계 (OTP 검증 및 완료)
  completeEmailRegister: (email: string, otp: string, terms: TermsAgreement) => Promise<void>;
  // OTP 재발송
  resendOTP: (email: string, lang?: string) => Promise<boolean>;
  // 이메일 로그인
  loginWithEmail: (email: string, password: string) => Promise<void>;
  // 비밀번호 재설정 요청
  requestPasswordReset: (email: string, lang?: string) => Promise<void>;
  // 비밀번호 재설정 완료
  confirmPasswordReset: (token: string, newPassword: string) => Promise<void>;
  // 비밀번호 설정 (OAuth 사용자)
  setPassword: (password: string) => Promise<void>;
  // 이메일 회원가입 진행 중 상태
  pendingEmailRegister: { email: string; name?: string } | null;
  setPendingEmailRegister: (data: { email: string; name?: string } | null) => void;
}

export interface GithubPreauth {
  preauthToken: string;
  email: string;
  name: string;
  picture?: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// 토큰 저장소
const TOKEN_KEY = 'auth_token';
const USER_KEY = 'auth_user';
const DELETE_FAREWELL_KEY = 'releasegate_show_delete_farewell';
const DELETE_FAREWELL_PAYLOAD_KEY = 'releasegate_delete_farewell_payload';

function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

function setStoredToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

function removeStoredToken(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

function getStoredUser(): User | null {
  const json = localStorage.getItem(USER_KEY);
  if (json) {
    try {
      return JSON.parse(json);
    } catch {
      return null;
    }
  }
  return null;
}

function setStoredUser(user: User): void {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}


// 개발용 플랜 오버라이드 저장 키
const DEV_PLAN_OVERRIDE_KEY = 'releasegate_dev_plan_override';

// Provider 컴포넌트
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [quotaData, setQuotaData] = useState<QuotaInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoggingIn, setIsLoggingIn] = useState(false);  // 로그인 처리 중 (딤 오버레이용)
  const [error, setError] = useState<string | null>(null);
  const [githubPreauth, setGithubPreauth] = useState<GithubPreauth | null>(null);
  // 이메일 회원가입 진행 중 상태
  const [pendingEmailRegister, setPendingEmailRegister] = useState<{ email: string; name?: string } | null>(null);
  // 개발용 플랜 오버라이드
  const [devPlanOverride, setDevPlanOverrideState] = useState<'free' | 'pro' | 'max' | 'team' | null>(() => {
    try {
      const stored = localStorage.getItem(DEV_PLAN_OVERRIDE_KEY);
      if (stored && ['free', 'pro', 'max', 'team'].includes(stored)) {
        return stored as 'free' | 'pro' | 'max' | 'team';
      }
    } catch {
      // ignore
    }
    return null;
  });

  // 개발용 플랜 오버라이드 설정
  const setDevPlanOverride = useCallback((plan: 'free' | 'pro' | 'max' | 'team' | null) => {
    setDevPlanOverrideState(plan);
    try {
      if (plan) {
        localStorage.setItem(DEV_PLAN_OVERRIDE_KEY, plan);
      } else {
        localStorage.removeItem(DEV_PLAN_OVERRIDE_KEY);
      }
    } catch {
      // ignore
    }
  }, []);

  // 플랜별 제한 정보 (오버라이드용) - pricing table 기준
  const getPlanLimits = useCallback((plan: 'free' | 'pro' | 'max' | 'team'): QuotaInfo['limits'] => {
    let result: QuotaInfo['limits'];
    switch (plan) {
      case 'free':
        result = {
          max_test_cases: 10,        // pricing: 최대 10개
          max_planning_chars: 3000,  // pricing: 3,000자
          history_limit: 3,          // pricing: 3건
          pdf_download: false,
          api_access: false,
          max_testcase_files: 1,
          max_planning_files: 1,
          personal_trend: false,
          team_trend: false,
          team_dashboard: false,
        };
        break;
      case 'pro':
        result = {
          max_test_cases: 100,       // pricing: 최대 100개
          max_planning_chars: 15000, // pricing: 15,000자
          history_limit: 10,         // pricing: 10건
          pdf_download: true,
          api_access: false,
          max_testcase_files: 3,
          max_planning_files: 3,
          personal_trend: false,
          team_trend: false,
          team_dashboard: false,
        };
        break;
      case 'max':
        result = {
          max_test_cases: 300,       // pricing: 최대 300개
          max_planning_chars: 30000, // pricing: 30,000자
          history_limit: 100,        // pricing: 100건
          pdf_download: true,
          api_access: false,
          max_testcase_files: 10,
          max_planning_files: 10,
          personal_trend: true,
          team_trend: false,
          team_dashboard: false,
        };
        break;
      case 'team':
        result = {
          max_test_cases: 300,       // pricing: 최대 300개
          max_planning_chars: 50000, // pricing: 50,000자
          history_limit: 100,        // pricing: 100건
          pdf_download: true,
          api_access: true,
          max_testcase_files: 10,
          max_planning_files: 10,
          personal_trend: true,
          team_trend: true,
          team_dashboard: true,
        };
        break;
    }
    return result;
  }, []);

  // 플랜별 월간 평가 제한 (오버라이드용)
  const getPlanMonthlyLimit = useCallback((plan: 'free' | 'pro' | 'max' | 'team'): { used: number; limit: number; unlimited: boolean } => {
    const used = quotaData?.monthly_evaluations.used || 0;
    switch (plan) {
      case 'free':
        return { used, limit: 3, unlimited: false };  // Free: 3회
      case 'pro':
        return { used, limit: 10, unlimited: false }; // Pro: 10회
      case 'max':
      case 'team':
        return { used, limit: -1, unlimited: true };  // Max/Team: 무제한
    }
  }, [quotaData]);

  // quota에 개발용 플랜 오버라이드 적용
  // quota에 개발용 플랜 오버라이드 적용
  const quota: QuotaInfo | null = quotaData ? (devPlanOverride ? {
    ...quotaData,
    actual_plan: quotaData.plan, // 실제 플랜 보존
    plan: devPlanOverride,
    limits: getPlanLimits(devPlanOverride),
    monthly_evaluations: getPlanMonthlyLimit(devPlanOverride),
  } : {
    ...quotaData,
    actual_plan: quotaData.plan,
  }) : null;

  // Quota 정보 가져오기
  const fetchQuota = useCallback(async () => {
    try {
      const token = getStoredToken();
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_BASE}/api/quota`, { headers });
      if (response.ok) {
        const data = await response.json();
        setQuotaData(data);
      }
    } catch {
      // quota 조회 실패해도 무시
    }
  }, []);

  // 초기화: 저장된 토큰으로 사용자 정보 복원
  useEffect(() => {
    const initAuth = async () => {
      try {
        // 1) redirect 기반 OAuth 결과 처리 (hash 사용)
        try {
          const hash = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : window.location.hash;
          if (hash) {
            const hp = new URLSearchParams(hash);
            const authToken = hp.get('auth_token');
            const termsPreauth = hp.get('terms_preauth');
            const mergePreauth = hp.get('merge_preauth');
            const provider = hp.get('provider');
            if (authToken) {
              setStoredToken(authToken);
              // hash 제거
              window.history.replaceState({}, '', `${window.location.pathname}${window.location.search}`);
            } else if (termsPreauth && provider === 'github') {
              // preauth 디코딩은 서버에서 하므로, UI 표시용 정보는 payload로부터 추정값을 못 가져옴
              // 콜백에서 preauth에 email/name/picture를 포함했으므로, 여기서는 서버 디코딩 없이도 최소 UI 표시를 위해
              // query/hash에 별도 정보를 넣지 않는 대신, 등록 요청 시 서버 응답으로 user를 받게 한다.
              // 다만 모달 상단 사용자 정보 표시를 위해 preauth 토큰에 포함된 정보를 클라에서도 디코딩(서명 검증 없음)한다.
              let info: { email?: string; name?: string; picture?: string | null } = {};
              try {
                const [, payloadB64] = termsPreauth.split('.');
                if (payloadB64) {
                  const b64 = payloadB64.replace(/-/g, '+').replace(/_/g, '/');
                  const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
                  const json = JSON.parse(atob(padded));
                  info = { email: json.email, name: json.name, picture: json.picture ?? null };
                }
              } catch {
                // ignore
              }
              setGithubPreauth({
                preauthToken: termsPreauth,
                email: info.email || '',
                name: info.name || 'GitHub',
                picture: info.picture ?? null,
              });
              window.history.replaceState({}, '', `${window.location.pathname}${window.location.search}`);
            } else if (mergePreauth && (provider === 'github' || provider === 'google')) {
              // merge는 프로필 모달에서 진행하도록 로컬스토리지에 저장
              try {
                localStorage.setItem('releasegate_merge_preauth', mergePreauth);
                localStorage.setItem('releasegate_merge_provider', provider);
                // 통합 모달이 보이도록 프로필 모달 열기 플래그도 세팅
                localStorage.setItem('releasegate_open_profile', '1');
              } catch {
                // ignore
              }
              window.history.replaceState({}, '', `${window.location.pathname}${window.location.search}`);
            }
          }
        } catch {
          // ignore
        }

        const token = getStoredToken();
        const storedUser = getStoredUser();

        if (token) {
          // token만 있어도 /me로 사용자 복원 (GitHub OAuth는 token만 내려오는 케이스가 많음)
          try {
            const response = await fetch(`${API_BASE}/api/auth/me`, {
              headers: {
                'Authorization': `Bearer ${token}`,
              },
            });

            if (response.ok) {
              const userData = await response.json();
              setUser(userData);
              setStoredUser(userData);
              setSentryUser(userData); // Sentry 사용자 정보 설정
              await fetchQuota();
            } else {
              // 토큰 만료/불일치 → 토큰 정리
              removeStoredToken();
              await fetchQuota();
            }
          } catch {
            // 네트워크 에러 시 캐시된 사용자 사용(있으면)
            if (storedUser) {
              setUser(storedUser);
            }
          }
        } else {
          // 비로그인 사용자
          await fetchQuota();
        }
      } finally {
        // 어떤 경우에도 로딩 상태 해제
        setIsLoading(false);
      }
    };
    
    initAuth();
  }, [fetchQuota]);

  const startGithubLogin = useCallback(() => {
    const returnTo = window.location.href;
    window.location.href = `${API_BASE}/api/integrations/github/auth/authorize?return_to=${encodeURIComponent(returnTo)}`;
  }, []);

  const completeGithubRegister = useCallback(async (preauthToken: string, terms: TermsAgreement) => {
    setIsLoading(true);
    setIsLoggingIn(true);  // 딤 오버레이 시작
    setError(null);
    try {
      const response = await fetch(`${API_BASE}/api/auth/github/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preauth_token: preauthToken, terms_agreement: terms }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'GitHub registration failed');
      }
      const data = await response.json();
      setStoredToken(data.access_token);
      setStoredUser(data.user);
      setUser(data.user);
      setSentryUser(data.user);
      setGithubPreauth(null);
      await fetchQuota();
    } catch (e) {
      const message = e instanceof Error ? e.message : 'GitHub registration failed';
      setError(message);
      throw e;
    } finally {
      setIsLoading(false);
      setIsLoggingIn(false);  // 딤 오버레이 종료
    }
  }, [fetchQuota]);

  const clearGithubPreauth = useCallback(() => {
    setGithubPreauth(null);
  }, []);

  // 다른 탭에서 로그인/로그아웃 시 현재 탭 동기화
  useEffect(() => {
    const handleStorageChange = async (event: StorageEvent) => {
      // auth_token이 변경되었을 때만 처리
      if (event.key !== TOKEN_KEY) return;
      
      if (event.newValue) {
        // 다른 탭에서 로그인됨 → 현재 탭도 로그인 상태로 전환
        const storedUser = getStoredUser();
        if (storedUser) {
          console.log('[StorageSync] Login detected from another tab');
          setUser(storedUser);
          setSentryUser(storedUser);
          await fetchQuota();
        }
      } else {
        // 다른 탭에서 로그아웃됨 → 현재 탭도 로그아웃
        console.log('[StorageSync] Logout detected from another tab');
        setUser(null);
        setQuotaData(null);
        setSentryUser(null);
        await fetchQuota(); // 비로그인 quota 가져오기
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [fetchQuota]);

  // 로그아웃
  const logout = useCallback(() => {
    removeStoredToken();
    setUser(null);
    setQuotaData(null);
    setSentryUser(null);  // Sentry 사용자 정보 초기화
    
    // 비로그인 상태 quota 다시 가져오기
    fetchQuota();
  }, [fetchQuota]);

  // API 호출에서 401(User not found/Invalid token 등) 발생 시: 즉시 토큰/유저 정리하여 UI가 복구되도록 함
  useEffect(() => {
    const handler = () => {
      removeStoredToken();
      setUser(null);
      setQuotaData(null);
      setSentryUser(null);
      fetchQuota();
    };
    window.addEventListener('releasegate:auth_invalid', handler as EventListener);
    return () => window.removeEventListener('releasegate:auth_invalid', handler as EventListener);
  }, [fetchQuota]);

  // 프로필 업데이트
  const updateProfile = useCallback(async (profile: ProfileUpdate) => {
    const token = getStoredToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    setError(null);
    
    try {
      const response = await fetch(`${API_BASE}/api/auth/profile`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(profile),
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Profile update failed');
      }
      
      const updatedUser = await response.json();
      setStoredUser(updatedUser);
      setUser(updatedUser);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Profile update failed';
      setError(message);
      throw e;
    }
  }, []);

  // 회원 탈퇴
  const deleteAccount = useCallback(async () => {
    const token = getStoredToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    setError(null);
    
    try {
      // 탈퇴 완료 모달에서 "함께한 날" 등 표시를 위해 최소 정보 보관
      // (탈퇴 성공 시 user/localStorage가 지워지므로 먼저 저장해둔다)
      const payload = {
        created_at: user?.created_at ?? null,
        name: user?.name ?? null,
        email: user?.email ?? null,
      };

      const response = await fetch(`${API_BASE}/api/auth/account`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Account deletion failed');
      }
      
      // 탈퇴 완료 메시지 모달 표시 플래그 (로그아웃 이후에도 App에서 표시)
      localStorage.setItem(DELETE_FAREWELL_KEY, '1');
      localStorage.setItem(DELETE_FAREWELL_PAYLOAD_KEY, JSON.stringify(payload));

      // 로컬 상태 정리
      removeStoredToken();
      setUser(null);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Account deletion failed';
      setError(message);
      throw e;
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const startLoggingIn = useCallback(() => {
    setIsLoggingIn(true);
  }, []);

  const cancelLoggingIn = useCallback(() => {
    setIsLoggingIn(false);
  }, []);

  // refreshQuota 래퍼
  const refreshQuota = useCallback(async () => {
    await fetchQuota();
  }, [fetchQuota]);

  // refreshUser - 서버에서 최신 사용자 정보 가져오기
  const refreshUser = useCallback(async () => {
    const token = getStoredToken();
    if (!token) return;

    try {
      const response = await fetch(`${API_BASE}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setStoredUser(userData);
      }
    } catch {
      // 실패해도 무시
    }
  }, []);

  // ===== 이메일/비밀번호 인증 함수들 =====
  
  // 이메일 존재 여부 확인
  const checkEmail = useCallback(async (email: string): Promise<EmailCheckResult> => {
    const response = await fetch(`${API_BASE}/api/auth/email/check`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || 'Email check failed');
    }
    
    return response.json();
  }, []);

  // 이메일 회원가입 1단계 (OTP 발송)
  const startEmailRegister = useCallback(async (
    email: string,
    password: string,
    name?: string,
    lang: string = 'ko'
  ): Promise<EmailRegisterResult> => {
    setError(null);
    
    const response = await fetch(`${API_BASE}/api/auth/email/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, name, lang }),
    });
    
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      const message = err.detail || 'Registration failed';
      setError(message);
      throw new Error(message);
    }
    
    const result = await response.json();
    setPendingEmailRegister({ email, name });
    return result;
  }, []);

  // 이메일 회원가입 2단계 (OTP 검증 및 완료)
  const completeEmailRegister = useCallback(async (
    email: string,
    otp: string,
    terms: TermsAgreement
  ): Promise<void> => {
    setIsLoading(true);
    setIsLoggingIn(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_BASE}/api/auth/email/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp, terms_agreement: terms }),
      });
      
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Verification failed');
      }
      
      const data = await response.json();
      setStoredToken(data.access_token);
      setStoredUser(data.user);
      setUser(data.user);
      setSentryUser(data.user);
      setPendingEmailRegister(null);
      await fetchQuota();
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Verification failed';
      setError(message);
      throw e;
    } finally {
      setIsLoading(false);
      setIsLoggingIn(false);
    }
  }, [fetchQuota]);

  // OTP 재발송
  const resendOTP = useCallback(async (email: string, lang: string = 'ko'): Promise<boolean> => {
    setError(null);
    
    try {
      const response = await fetch(`${API_BASE}/api/auth/email/resend-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, lang }),
      });
      
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Resend failed');
      }
      
      const result = await response.json();
      return result.success;
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Resend failed';
      setError(message);
      throw e;
    }
  }, []);

  // 이메일 로그인
  const loginWithEmail = useCallback(async (email: string, password: string): Promise<void> => {
    setIsLoading(true);
    setIsLoggingIn(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_BASE}/api/auth/email/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Login failed');
      }
      
      const data = await response.json();
      setStoredToken(data.access_token);
      setStoredUser(data.user);
      setUser(data.user);
      setSentryUser(data.user);
      await fetchQuota();
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Login failed';
      setError(message);
      throw e;
    } finally {
      setIsLoading(false);
      setIsLoggingIn(false);
    }
  }, [fetchQuota]);

  // 비밀번호 재설정 요청
  const requestPasswordReset = useCallback(async (email: string, lang: string = 'ko'): Promise<void> => {
    setError(null);
    
    const response = await fetch(`${API_BASE}/api/auth/password/reset-request`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, lang }),
    });
    
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || 'Password reset request failed');
    }
  }, []);

  // 비밀번호 재설정 완료
  const confirmPasswordReset = useCallback(async (token: string, newPassword: string): Promise<void> => {
    setIsLoading(true);
    setIsLoggingIn(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_BASE}/api/auth/password/reset-confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: newPassword }),
      });
      
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Password reset failed');
      }
      
      const data = await response.json();
      setStoredToken(data.access_token);
      setStoredUser(data.user);
      setUser(data.user);
      setSentryUser(data.user);
      await fetchQuota();
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Password reset failed';
      setError(message);
      throw e;
    } finally {
      setIsLoading(false);
      setIsLoggingIn(false);
    }
  }, [fetchQuota]);

  // 비밀번호 설정 (OAuth 사용자)
  const setPassword = useCallback(async (password: string): Promise<void> => {
    const token = getStoredToken();
    if (!token) {
      throw new Error('Not authenticated');
    }
    
    setError(null);
    
    try {
      const response = await fetch(`${API_BASE}/api/auth/password/set`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ password }),
      });
      
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Password set failed');
      }
      
      const updatedUser = await response.json();
      setStoredUser(updatedUser);
      setUser(updatedUser);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Password set failed';
      setError(message);
      throw e;
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        quota,
        isLoading,
        isLoggingIn,
        startLoggingIn,
        cancelLoggingIn,
        isAuthenticated: !!user,
        logout,
        startGithubLogin,
        githubPreauth,
        completeGithubRegister,
        clearGithubPreauth,
        updateProfile,
        deleteAccount,
        refreshQuota,
        refreshUser,
        error,
        clearError,
        devPlanOverride,
        setDevPlanOverride,
        // 이메일/비밀번호 인증
        checkEmail,
        startEmailRegister,
        completeEmailRegister,
        resendOTP,
        loginWithEmail,
        requestPasswordReset,
        confirmPasswordReset,
        setPassword,
        pendingEmailRegister,
        setPendingEmailRegister,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// Hook
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

